import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { _className, Button, Grid, LL_H_C, LL_V_L, openContent, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { TS_EditableItemComponent } from '@nu-art/thunderstorm-frontend/components/TS_EditableItemComponent/TS_EditableItemComponent';
import './editors.scss';
import { Editor_Atom } from './Editor_Atom.js';
import { _keys, deepClone, exists, reduceToMap } from '@nu-art/ts-common';
import { Component_StructuredComplexLogic } from '../structured-complex-logic/Component_StructuredComplexLogic.js';
import { ModuleFE_Expression } from '../../../../../../_entity.js';
import { QRangeInputV3 } from '../../../../../q-components/QRangeInputV3.js';
import { DefaultEditor_InputText_Optional } from '@app/styles/frontend/ui-manager/ui-components';
import { DropDown_ExpressionOutputTypes } from '../../../../../../_entity/expression/ui-components.js';
import { ICONS } from '@app/styles';
import { TS_ListOrganizer } from '@nu-art/thunderstorm-frontend/components/TS_ListOrganizer/index';
import { CollectLabelSwaps, updateExpressionString } from './utils.js';
import { AtomsLogicTypes, BasicAtomsLogicTypes, ExpressionOutput_Range, ExpressionType_NormalRange, LogicType_Add, LogicType_And, LogicType_Complex, LogicType_GreaterOrEqual, LogicType_StructuredFreeText } from '@app/hcs-shared/expression';
import { getEmptyTerm } from '../../../../../../_entity/expression/utils.js';
export const actionSubTypeLabelMap = {
    '&&': 'AND',
    '||': 'OR',
    '+': 'SUM',
    '?': 'COMPLEX',
    '>_': 'FREE TEXT',
    '>=': 'AT LEAST'
};
const EmptyString = '';
export class Editor_Term extends TS_EditableItemComponent {
    deriveStateFromProps(nextProps, state) {
        state = super.deriveStateFromProps(nextProps, state);
        state.viewMode = nextProps.viewMode ?? false;
        const value = state.editable.get('value');
        const fallbackValue = state.editable.get('fallbackValue');
        state.toggleTermValue = (exists(value) || exists(fallbackValue)) ? true : nextProps.isSubTerm ? nextProps.isSubTerm : false;
        state.prevSubTerms = state.editable.item.subTerms ?? state.prevSubTerms;
        return state;
    }
    async componentDidMount() {
        window.addEventListener('expression-clipboard-changed', this.handleClipboardEvent);
        const hasCopiedAtom = !!(await ModuleFE_Expression.getCopiedAtom());
        if (this.mounted)
            this.setState({ hasCopiedAtom });
    }
    componentWillUnmount() {
        window.removeEventListener('expression-clipboard-changed', this.handleClipboardEvent);
    }
    handleClipboardEvent = async () => {
        const hasCopiedAtom = !!(await ModuleFE_Expression.getCopiedAtom());
        if (this.mounted)
            this.setState({ hasCopiedAtom });
    };
    //######################### Logic #########################
    setAtomsLogic = async () => {
        if (this.props.viewMode)
            return;
        const _term = this.state.editable;
        const atomsLogic = _term.get('atomsLogic');
        const availableOperators = this.props.isSubTerm ? BasicAtomsLogicTypes : AtomsLogicTypes;
        const changes = {};
        //Set the next available atoms logic in the changes object
        const index = availableOperators.indexOf(atomsLogic);
        changes.atomsLogic = availableOperators[index + 1] ? availableOperators[index + 1] : availableOperators[0];
        if (ModuleFE_Expression.isTerm.withSubTerms(_term)) {
            // When not using complex logic output type is redundant
            if (_term.get('outputType'))
                changes.outputType = undefined;
            changes.subTerms = undefined;
            changes.fallbackValue = undefined;
        }
        //Make sure to use previous sub terms if not deleted when next atom logic is complex
        if (changes.atomsLogic === LogicType_Complex) {
            changes.subTerms = this.state.prevSubTerms ?? [getEmptyTerm()];
            changes.fallbackValue = [0, 0];
        }
        //Remove term value if next atom is "+" and value exists
        if (changes.atomsLogic === LogicType_Add && exists(_term.get('value'))) {
            changes.value = undefined;
            changes.fallbackValue = undefined;
            this.setState({ toggleTermValue: false });
        }
        //Add empty free text object in case it's the next logic type
        if (changes.atomsLogic === LogicType_StructuredFreeText) {
            changes.structuredFreeText = { if: undefined, then: undefined, else: undefined };
        }
        // Make sure to remove all irrelevant data when switching from structured free text
        if (atomsLogic === LogicType_StructuredFreeText) {
            changes.structuredFreeText = undefined;
            changes.outputType = undefined;
        }
        if (atomsLogic === LogicType_GreaterOrEqual) {
            changes.structuredFreeText = undefined;
            changes.value = undefined;
        }
        // update the expression after the change
        changes.expression = this.updateTerm(deepClone(_term.get('atoms')) ?? [], changes.atomsLogic);
        await this.state.editable.updateObj(changes);
    };
    deleteAtom = async (index) => {
        const atoms = (this.state.editable.get('atoms') ?? []);
        atoms?.splice(index, 1);
        if (!atoms?.length)
            return this.props.deleteTerm();
        const changes = {
            atoms: atoms,
            expression: this.updateTerm(atoms)
        };
        // No atomLogic when there's only one atom
        if (atoms.length === 1) {
            changes.value = undefined;
            changes.fallbackValue = undefined;
            changes.atomsLogic = undefined;
            if (exists(this.state.editable.item.subTerms)) {
                changes.subTerms = undefined;
            }
        }
        await this.state.editable.updateObj(changes);
        //FIXME: hack, don't manage to understand why this updateObj call doesn't trigger the re-derive
        this.forceUpdate(() => this.state.editable.save());
    };
    addEmptyAtom = async () => {
        const termItem = this.state.editable;
        const changes = {};
        //Add empty atom to the changes object
        changes.atoms = [...deepClone(termItem.get('atoms')) ?? [], {
                cases: [{}],
                leftHand: undefined
            }];
        //Add atomLogic when there's no previous one and there's more than one atom
        if (changes.atoms.length > 1 && !exists(this.state.editable.get('atomsLogic')))
            changes.atomsLogic = LogicType_And;
        //Update the string expression
        changes.expression = this.updateTerm(changes.atoms, changes.atomsLogic);
        await termItem.updateObj(changes);
    };
    pasteAtom = async () => {
        const clipboardAtom = await ModuleFE_Expression.getCopiedAtom();
        if (!clipboardAtom)
            return;
        const termItem = this.state.editable;
        const changes = {};
        //Append the pasted atom to the changes object
        changes.atoms = [...deepClone(termItem.get('atoms')) ?? [], clipboardAtom.data];
        //Add atomLogic when there's no previous one and there's more than one atom
        if (changes.atoms.length > 1 && !exists(this.state.editable.get('atomsLogic')))
            changes.atomsLogic = LogicType_And;
        //Update the string expression
        changes.expression = this.updateTerm(changes.atoms, changes.atomsLogic);
        await termItem.updateObj(changes);
    };
    updateTerm = (atomsArray, atomsLogic) => {
        const termItem = this.state.editable;
        const _atomsLogic = atomsLogic ?? termItem.get('atomsLogic');
        const expression = termItem.get('expression');
        if (!atomsArray || _atomsLogic === LogicType_Complex || _atomsLogic === LogicType_StructuredFreeText)
            return EmptyString;
        const atoms = atomsArray.map((_, index) => `A${index + 1}`);
        if (!atoms.length)
            return expression;
        // Format for at least expression
        if (_atomsLogic === LogicType_GreaterOrEqual) {
            const range = termItem.get('value') ?? termItem.get('fallbackValue'); // [N,N]
            const n = Array.isArray(range) ? range[0] : undefined;
            const sum = atoms.join(' + ');
            return n !== undefined ? `${sum} >= ${n}` : sum;
        }
        // default
        return `${atoms.join(` ${_atomsLogic} `)}`;
    };
    toggleAddValue = () => {
        const value = this.state.editable.get('value');
        const fallbackValue = this.state.editable.get('fallbackValue');
        this.setState({ toggleTermValue: !this.state.toggleTermValue }, async () => {
            if (!this.state.toggleTermValue && (exists(value) || exists(fallbackValue)))
                return this.state.editable.updateObj({ value: undefined, fallbackValue: undefined });
            return this.state.editable.updateObj({ fallbackValue: [0, 0] });
        });
    };
    getUnusedAtoms = () => {
        //Fail fast in case it's not term with sub-terms, check is irrelevant
        if (!ModuleFE_Expression.isTerm.withSubTerms(this.state.editable))
            return undefined;
        const atoms = this.state.editable.get('atoms');
        const subTerms = this.state.editable.get('subTerms');
        return atoms.filter((atom, index) => {
            return !subTerms.some(subTerm => !!subTerm.atoms.find(atom => atom.leftHand?.id === String(index + 1)));
        });
    };
    canAddNewAtom = () => {
        //If the expression is not normal range this restriction is irrelevant
        if (this.props.expressionType !== ExpressionType_NormalRange.dbKey)
            return true;
        //If there's no value to the first atom
        const firstAtomValue = this.state.editable.get('atoms')[0].cases[0].value;
        if (!firstAtomValue)
            return true;
        //Can add new atom only when the value is not range
        return firstAtomValue[0] === firstAtomValue[1];
    };
    canAddTermValue = () => {
        //SubTerm can't toggle term value, it's always on
        if (this.props.isSubTerm)
            return false;
        const isAtLeastExpression = this.state.editable.item.atomsLogic === LogicType_GreaterOrEqual;
        if (isAtLeastExpression)
            return false;
        //Add and free text logic can't have term value, value determined by the atoms values and in free text return and else values are strings
        const atomsLogic = this.state.editable.get('atomsLogic');
        if (atomsLogic === LogicType_Add || atomsLogic === LogicType_StructuredFreeText)
            return false;
        //Term with one atom that has value can't add term value it's redundant
        const atoms = this.state.editable.get('atoms');
        const termValue = this.state.editable.get('value');
        const termFallbackValue = this.state.editable.get('fallbackValue');
        if (atoms.length === 1 && exists(atoms[0].cases[0].value) && (!exists(termValue) && !exists(termFallbackValue)))
            return false;
        //Term with sub terms can't change this filed as well
        return !ModuleFE_Expression.isTerm.withSubTerms(this.state.editable);
    };
    handleDnD = async (newOrder, oldOrder) => {
        const atoms = newOrder.map(orderElement => orderElement.editable.item);
        const atomsLogic = this.state.editable.get('atomsLogic');
        const expression = this.updateTerm(atoms);
        //Handle free text edge case
        if (LogicType_StructuredFreeText === atomsLogic) {
            const structuredFreeText = deepClone(this.state.editable.get('structuredFreeText'));
            // update each prop with the new label order
            _keys(structuredFreeText).forEach(key => {
                structuredFreeText[key] = updateExpressionString(CollectLabelSwaps(newOrder, oldOrder), structuredFreeText[key]);
            });
            await this.state.editable.updateObj({ atoms, structuredFreeText, expression });
            return;
        }
        //Handle sub-terms edge case
        if (LogicType_Complex === atomsLogic) {
            const labelSwap = CollectLabelSwaps(newOrder, oldOrder).map(swap => reduceToMap(_keys(swap), item => item, item => swap[item].replace(/([A-Za-z]+)(\d+)/g, '$2')));
            const newSubTerms = deepClone(this.state.editable.item.subTerms).map(subTerm => {
                subTerm.atoms = subTerm.atoms.map(atom => {
                    const swapNew = labelSwap.find(swap => swap.oldLabel === atom.leftHand.id);
                    const swapOld = labelSwap.find(swap => swap.newLabel === atom.leftHand.id);
                    if (!swapNew && !swapOld)
                        return atom;
                    if (swapNew)
                        atom.leftHand.id = swapNew.newLabel;
                    if (swapOld)
                        atom.leftHand.id = swapOld.oldLabel;
                    return atom;
                });
                return subTerm;
            });
            await this.state.editable.updateObj({
                subTerms: newSubTerms,
                atoms,
                expression
            });
            return;
        }
        await this.state.editable.updateObj({ atoms, expression });
    };
    //######################### Render - Atoms #########################
    renderAtomLabel = (index, isUnused, isDragged, onDragStart, onDragEnd) => {
        if (this.props.isSubTerm)
            return '';
        const className = _className('atom-label-container', isDragged && 'grabbing ');
        const labelClassName = _className('atom-label', isUnused && 'warning', !this.state.viewMode && 'copyable');
        const shouldRender = this.state.editable.item.atoms?.length > 1 && !this.state.viewMode;
        const labelTooltip = isUnused
            ? openContent.tooltip.top('warning-tooltip', () => _jsx(_Fragment, { children: "Atom is not in used in the sub terms" }), { offset: 6 })
            : (!this.state.viewMode ? openContent.tooltip.top('atom-copy-tooltip', () => _jsx(_Fragment, { children: "Copy atom" }), { offset: 6 }) : {});
        return _jsxs(LL_H_C, { className: className, children: [shouldRender && _jsx(ICONS.dragHandle.component, { onDragStart: e => {
                        const container = e.currentTarget.parentNode.parentNode.parentNode;
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/html', container);
                        e.dataTransfer.setDragImage(container, 30, 30);
                        onDragStart(e, index);
                    }, onDragEnd: onDragEnd, draggable: true }), _jsx("div", { className: labelClassName, ...labelTooltip, onClick: this.state.viewMode ? undefined : async () => {
                        await ModuleFE_Expression.copyAtomToClipboard(this.state.editable.item.atoms[index], index);
                        this.setState({ hasCopiedAtom: true });
                    }, children: `A${index + 1}:` })] });
    };
    renderAtom = (props) => {
        const atoms = (this.props.isSubTerm && this.props.termAtoms) ? this.props.termAtoms : undefined;
        const unusedAtoms = this.getUnusedAtoms();
        const isUnused = !!unusedAtoms?.find(atoms => atoms.leftHand?.id === props.item.editable.get('leftHand')?.id);
        return _jsx(TS_PropRenderer.Horizontal, { onDragOver: e => props.onDragOver(e, props.index), onDragLeave: e => props.onDragLeave(e, props.index), label: this.renderAtomLabel(props.index, isUnused, props.dragged, props.onDragStart, props.onDragEnd), className: "match_width", children: _jsx(LL_V_L, { className: 'match_parent flex__grow', style: { gap: '5px' }, children: _jsx(Editor_Atom, { usedVarIds: this.props.usedVarIds, deleteAtom: () => this.deleteAtom(props.index), editable: props.item.editable, atoms: atoms, viewMode: this.state.viewMode }, `atom-${props.index}`) }) }, props.index);
    };
    renderAtoms = () => {
        const editableAtomsArray = this.state.editable.editProp('atoms', []);
        const editableAtoms = editableAtomsArray.item.map((term, index) => {
            return { editable: editableAtomsArray.editProp(index, {}), label: `A${index + 1}` };
        });
        return _jsxs(Grid, { className: 'condition-container', children: [_jsx(TS_ListOrganizer, { items: editableAtoms, renderer: this.renderAtom, onOrderChanged: (newOrder) => this.handleDnD(newOrder, editableAtoms) }), this.renderTermValue(), this.renderAtLeastValue(), this.renderButtons()] });
    };
    //######################### Render - Editor Body #########################
    renderButtons = () => {
        if (this.state.viewMode)
            return '';
        const addButtonLabel = this.state.toggleTermValue ? 'Remove Term Value' : 'Add Term Value';
        const addAtomLabel = this.props.isSubTerm ? 'Add Option' : 'Add Atom';
        const className = _className('button-container', this.props.isSubTerm && 'no-margin');
        return _jsxs(LL_H_C, { className: className, children: [this.canAddNewAtom() && _jsx(Button, { variant: 'secondary', onClick: this.addEmptyAtom, className: 'add-entity-btn', children: addAtomLabel }), !this.props.isSubTerm && this.canAddNewAtom() && _jsx(Button, { variant: 'secondary', disabled: !this.state.hasCopiedAtom, onClick: this.pasteAtom, className: 'add-entity-btn', children: "Paste Atom" }), this.canAddTermValue() && _jsx(Button, { variant: 'secondary', onClick: this.toggleAddValue, className: 'add-entity-btn', children: addButtonLabel })] });
    };
    renderTermValue = () => {
        const { toggleTermValue, editable } = this.state;
        const { isSubTerm, viewMode } = this.props;
        const atomsLogic = editable.get('atomsLogic');
        if (!toggleTermValue && atomsLogic !== LogicType_Complex)
            return;
        if (atomsLogic === LogicType_GreaterOrEqual)
            return;
        const termValueLabel = isSubTerm ? 'Sub-Term Value:' : 'Term Value:';
        const termFallbackValueLabel = 'Term Else Value:';
        const rowClassName = _className('term-editor__value-input', isSubTerm && 'no-margin');
        const containerClassName = _className('value-input__input-container');
        return _jsxs(LL_V_L, { style: { gap: '5px' }, children: [atomsLogic !== LogicType_Complex && _jsxs(LL_H_C, { className: rowClassName, children: [_jsx("div", { className: 'value-input__value-label', children: termValueLabel }), _jsxs(LL_H_C, { className: containerClassName, children: [_jsx("div", { className: 'divider' }), _jsx(QRangeInputV3, { placeholder: "Value", editable: editable, prop: 'value', disabled: viewMode, showErrorTooltip: true })] })] }), !isSubTerm && _jsxs(LL_H_C, { className: rowClassName, children: [_jsx("div", { className: 'value-input__value-label', children: termFallbackValueLabel }), _jsxs(LL_H_C, { className: containerClassName, children: [_jsx("div", { className: 'divider' }), _jsx(QRangeInputV3, { placeholder: "Value", editable: editable, disabled: viewMode, prop: 'fallbackValue', showErrorTooltip: true })] })] })] });
    };
    renderAtLeastValue = () => {
        const { editable } = this.state;
        const { viewMode } = this.props;
        const atomsLogic = editable.get('atomsLogic');
        if (atomsLogic !== LogicType_GreaterOrEqual)
            return;
        const atomsString = editable.item.atoms?.map((atom, index) => `A${index + 1}`).join(' + ');
        const atLeastLabel = `${atomsString} is at least:`;
        return _jsx(LL_V_L, { style: { gap: '5px' }, children: _jsxs(LL_H_C, { className: 'term-editor__value-input', children: [_jsx("div", { className: 'value-input__value-label bold', children: atLeastLabel }), _jsxs(LL_H_C, { className: 'value-input__input-container', children: [_jsx("div", { className: 'divider' }), _jsx(QRangeInputV3, { placeholder: "Value", editable: editable, prop: 'value', disabled: viewMode, showErrorTooltip: true })] })] }) });
    };
    renderSubTermsEditor = () => {
        const editable = this.state.editable;
        if (!ModuleFE_Expression.isTerm.withSubTerms(editable))
            return '';
        return _jsx(Component_StructuredComplexLogic, { resetExpression: () => this.setState({ prevSubTerms: undefined }, async () => {
                const changes = {};
                const atoms = editable.get('atoms');
                if (atoms.length > 1)
                    changes.atomsLogic = LogicType_And;
                changes.subTerms = undefined;
                changes.fallbackValue = undefined;
                changes.expression = this.updateTerm((atoms ?? []), changes.atomsLogic);
                await editable.updateObj(changes);
            }), editable: editable });
    };
    renderFreeTextInput = (editableFreeText, keyToEdit) => {
        return _jsx(DefaultEditor_InputText_Optional, { editable: editableFreeText, prop: keyToEdit, saveEvent: ['blur'], onChange: value => {
                return editableFreeText.update(keyToEdit, value?.length ? value : undefined);
            }, showErrorTooltip: true });
    };
    renderStructuredFreeText = () => {
        const atomsLogic = this.state.editable.get('atomsLogic');
        if (atomsLogic !== LogicType_StructuredFreeText)
            return '';
        const editableFreeText = this.state.editable.editProp('structuredFreeText', {});
        return _jsxs(LL_H_C, { className: 'term-editor__structured-free-text', children: [_jsx(TS_PropRenderer.Horizontal, { label: 'If:', children: this.renderFreeTextInput(editableFreeText, 'if') }), _jsx(TS_PropRenderer.Horizontal, { label: 'Then:', children: this.renderFreeTextInput(editableFreeText, 'then') }), _jsx(TS_PropRenderer.Horizontal, { label: 'Else:', children: this.renderFreeTextInput(editableFreeText, 'else') }), _jsx(TS_PropRenderer.Horizontal, { label: 'Output Type:', children: _jsx(DropDown_ExpressionOutputTypes.editable, { showErrorTooltip: true, queryFilter: item => {
                            const isNormalRange = this.props.expressionType === ExpressionType_NormalRange.dbKey;
                            return isNormalRange ? item === ExpressionOutput_Range : item !== ExpressionOutput_Range;
                        }, editable: this.state.editable, prop: 'outputType' }) })] });
    };
    renderAtomsLogic = () => {
        const termItem = this.state.editable.item;
        const AtomsLength = termItem.atoms?.length ?? 0;
        const operator = termItem.atomsLogic;
        if (AtomsLength <= 1 || !operator)
            return '';
        const className = _className('logic-picker', operator === LogicType_And ? 'and' : 'or', this.props.viewMode && 'disabled');
        return _jsx(LL_V_L, { className: 'bridge', children: _jsx(LL_H_C, { className: className, onClick: this.setAtomsLogic, children: actionSubTypeLabelMap[operator] }) });
    };
    renderBody = () => {
        return _jsxs(LL_H_C, { className: 'match_parent flex__grow', children: [this.renderAtomsLogic(), this.renderAtoms()] });
    };
    render() {
        const className = _className('term');
        return (_jsxs(LL_V_L, { className: className, children: [this.renderBody(), this.renderSubTermsEditor(), this.renderStructuredFreeText()] }));
    }
}
//# sourceMappingURL=Editor_Term.js.map